import { PropTypes } from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Form, InputNumber, Table, Typography } from 'antd';
import { formatDate } from '../../utils/date';
import { APP_TERMS } from '../../app-terms';
import { useUserLocale } from '../../hooks/useLocale';
import StrikeThroughText from './StrikeThroughText';

const { Text } = Typography;
export const EditableContext = React.createContext(null);
export const EditableRow = ({ ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};
EditableRow.propTypes = {
  index: PropTypes.number,
};
export const EditableCell = ({
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);
  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };
  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({
        ...record,
        ...values,
      });
    } catch (errInfo) {
      handleSave({
        ...record,
        [dataIndex]: 0,
      });
    }
  };
  let childNode = children;
  if (editable) {
    childNode =
      editing && !record.isDeleted ? (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: dataIndex === 'rushFee' ? false : true,
              message: `required.`,
              validator: (_, value) => {
                if (dataIndex === 'rushFee') return Promise.resolve();

                if (!value) {
                  return Promise.reject('invalid');
                }
                return Promise.resolve();
              },
            },
          ]}
          initialValue={record[dataIndex]}
        >
          <InputNumber ref={inputRef} onPressEnter={save} onBlur={save} />
        </Form.Item>
      ) : record?.isDeleted ? (
        <StrikeThroughText strikeThrough value={children} />
      ) : (
        <div
          className="editable-cell-value-wrap"
          style={{
            paddingInlineEnd: 24,
          }}
          onClick={toggleEdit}
        >
          {children}
        </div>
      );
  }
  return <td {...restProps}>{childNode}</td>;
};
EditableCell.propTypes = {
  title: PropTypes.string,
  editable: PropTypes.bool,
  children: PropTypes.node,
  dataIndex: PropTypes.string,
  record: PropTypes.object,
  handleSave: PropTypes.func,
};
const EditRadiologistInvoiceService = ({ onChange, value }) => {
  const [dataSource, setDataSource] = useState(value);
  const userLocale = useUserLocale();

  const defaultColumns = [
    {
      title: 'Case ID',
      dataIndex: 'caseId',
      render: (text, record) => {
        return (
          <StrikeThroughText strikeThrough={record?.isDeleted} value={text} />
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'casePatientName',
      render: (text, record) => {
        return (
          <StrikeThroughText strikeThrough={record?.isDeleted} value={text} />
        );
      },
    },
    {
      title: 'Case Date',
      dataIndex: 'caseCreatedAt',
      render: (text, record) => {
        const value = formatDate(text, APP_TERMS.date_1_format, userLocale);
        return (
          <StrikeThroughText strikeThrough={record?.isDeleted} value={value} />
        );
      },
    },
    {
      title: 'Unit Price($)',
      dataIndex: 'amount',
      editable: false,
      align: 'right',
    },
  ];

  useEffect(() => {
    console.log('dataSource : ', dataSource);
  }, [dataSource]);
  const handleSave = (row) => {
    const newData = [...dataSource];
    console.log("newData : ", newData);
    const index = newData.findIndex((item) => row.invoiceServiceId === item.invoiceServiceId);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
    onChange?.(newData);
  };
  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };
  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });
  return (
    <div>
      <Table
        components={components}
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        rowKey={(record) => record?.invoiceServiceId}
        summary={(pageData) => {
          let totalAmount = 0;

          pageData.forEach(({ amount, isDeleted }) => {
            if (!isDeleted) {
              totalAmount += amount || 0;
            }
          });

          return (
            <Table.Summary.Row
              className="bg-light"
              style={{ fontWeight: 'bolder' }}
            >
              <Table.Summary.Cell index={0} colSpan={3}>
                Total
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} colSpan={1} align="right">
                <Text type="danger">{totalAmount}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
    </div>
  );
};
EditRadiologistInvoiceService.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.array,
};
export default EditRadiologistInvoiceService;
